import { TabContext, TabPanel } from '@mui/lab';
import { Container, Stack, Typography, useTheme } from '@mui/material';
import { SyntheticEvent, useCallback, useState } from 'react';
import { groupBy } from 'remeda';
import { DynamicIcon, NamedLayoutBlock } from 'src/components';
import { SignalUrgencyLevel, useSignalBusinessHours } from 'src/graphql';
import { useManageUrgencyLevel } from 'src/graphql/entities/signal-business-hours/hooks/use-manage-urgency-level';
import { StyledTab, StyledTabList } from './styled';
import { UrgencyLevel, UrgencyTab } from './urgency-tab';

export const EscalationsView = () => {
  const { signalBusinessHours, isLoading } = useSignalBusinessHours();
  const [disabledUrgencyLevels, setDisabledUrgencyLevels] = useManageUrgencyLevel();
  const theme = useTheme();

  const tabs: UrgencyLevel[] = [
    {
      name: 'Normal',
      description: 'Routine incidents that can be addressed during standard operating hours',
      urgency: SignalUrgencyLevel.Normal,
      icon: 'signal_cellular_3_bar',
      iconProps: {
        color: theme.palette.custom.tertiary.tertiary60,
      },
      canDisable: false,
    },
    {
      name: 'Urgent',
      description:
        'Time-sensitive incidents requiring prompt attention and expedited response',
      urgency: SignalUrgencyLevel.Urgent,
      icon: 'warning',
      iconProps: {
        color: theme.palette.warning.main,
      },
    },
    {
      name: 'Life-Safety',
      description:
        'Critical incidents involving potential safety risks requiring immediate response',
      urgency: SignalUrgencyLevel.LifeSafety,
      icon: 'e911_emergency',
      iconProps: {
        color: theme.palette.error.main,
      },
    },
  ];

  const [activeTab, setActiveTab] = useState(SignalUrgencyLevel.Normal);
  const signalBusinessHoursByUrgency = groupBy(signalBusinessHours, (item) => item.urgency);

  const getSignalBusinessHoursByUrgency = useCallback(
    (urgency: SignalUrgencyLevel) => signalBusinessHoursByUrgency[urgency] || [],
    [signalBusinessHoursByUrgency],
  );

  const onTabChange = useCallback((_: SyntheticEvent, value: SignalUrgencyLevel) => {
    setActiveTab(value);
  }, []);

  const onUrgencyLevelToggleChange = useCallback(
    async (urgency: SignalUrgencyLevel, value: boolean) => {
      await setDisabledUrgencyLevels(urgency, !value); // Invert checkbox value as we need disable option when it's unchecked.
    },
    [setDisabledUrgencyLevels],
  );

  return (
    <NamedLayoutBlock
      title="Escalation Window"
      sx={{
        flex: 1,
        display: { sm: 'flex' },
      }}
    >
      <Stack direction="row" justifyContent="center" padding={5}>
        <Container maxWidth="md">
          <Typography variant="h5">Escalation Window</Typography>
          <Typography variant="body2" mt={1} mb={2}>
            Configure when different urgency levels should be escalated. Each urgency level can
            have its own schedule with multiple time windows per day.
          </Typography>
          <TabContext value={activeTab}>
            <StyledTabList onChange={onTabChange} variant="fullWidth">
              {tabs.map(({ name, urgency, icon, iconProps }) => (
                <StyledTab
                  key={urgency}
                  label={name}
                  value={urgency}
                  icon={<DynamicIcon name={icon} {...iconProps} />}
                  iconPosition="start"
                />
              ))}
            </StyledTabList>
            {tabs.map(({ urgency, ...rest }) => (
              <TabPanel key={urgency} value={urgency}>
                <UrgencyTab
                  onUrgencyEnabledChange={onUrgencyLevelToggleChange}
                  enabled={!disabledUrgencyLevels.includes(urgency)}
                  signalBusinessHours={getSignalBusinessHoursByUrgency(urgency)}
                  urgency={urgency}
                  {...rest}
                  isLoading={isLoading}
                />
              </TabPanel>
            ))}
          </TabContext>
        </Container>
      </Stack>
    </NamedLayoutBlock>
  );
};
